/**
 * usePortalPanelConfig — R3 AC5: the header panel's capability-derived tab set.
 * SPEC-stakeholder-portal-presentation-redesign-2026-07-20, R3.
 *
 * Fetched in PARALLEL with the first tab's own content query (My Submissions'
 * `usePortalUnseenCount`/issues query already fires independently, gated only
 * on the bell's own `visible` check — NOT on this config resolving first), so
 * the panel never pays a serial round-trip before showing content (the
 * async-offload constraint this spec's cross-cutting section preserves).
 *
 * Bounded by an AbortController timeout SHORTER than the panel's own load
 * budget: if panel-config is slow/hung, the panel still opens on schedule
 * with a FAIL-CLOSED fallback (base client tabs only — never surfaces
 * super-admin tabs speculatively). A slow/failed config fetch degrades the
 * panel's breadth, never its privacy posture.
 */
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { PortalPanelConfigResponse } from '@/lib/schemas';

export const PORTAL_PANEL_CONFIG_QUERY_KEY = ['stakeholder-portal', 'panel-config'] as const;

/** The panel's own render budget — config resolution must never approach this. */
export const PANEL_LOAD_BUDGET_MS = 5_000;
/** Strictly shorter than the load budget (R3 AC5's explicit requirement). */
export const PANEL_CONFIG_TIMEOUT_MS = 2_000;

/** Fail-closed default: only the base client tabs, never a super-admin tab
 *  speculatively rendered while config is unresolved/unavailable. */
export const PANEL_CONFIG_FALLBACK: PortalPanelConfigResponse = {
  tabs: ['my_submissions', 'roadmap'],
  is_super_admin: false,
};

export function usePortalPanelConfig(enabled: boolean) {
  return useQuery({
    queryKey: PORTAL_PANEL_CONFIG_QUERY_KEY,
    queryFn: async ({ signal }) => {
      // Bound this specific request to PANEL_CONFIG_TIMEOUT_MS, independent of
      // react-query's own retry/staleTime knobs — a hung request must not hold
      // the panel's tab bar in an unresolved state past the timeout.
      const timeoutController = new AbortController();
      const timeoutId = setTimeout(() => timeoutController.abort(), PANEL_CONFIG_TIMEOUT_MS);
      // Combine the query's own abort signal (unmount/refetch) with the timeout.
      signal?.addEventListener('abort', () => timeoutController.abort());
      try {
        return await api.stakeholderPortal.panelConfig(timeoutController.signal);
      } finally {
        clearTimeout(timeoutId);
      }
    },
    enabled,
    staleTime: 60_000,
    retry: false,
  });
}
